import { useEffect, useRef, useState } from 'react'
import { Column, Row } from 'react-table'
import cx from 'classnames'

import { containsRtlChars } from 'common/helpers/string'
import { useValidationError, Validations } from 'common/hooks/custom'
import {
  replaceLineBreakAsNewLineEmoji,
  replaceNewLineEmojiAsString,
} from 'common/variantsUtils'

import type { OnCellChange } from '../types/interfaces'

// TODO: Create shared component for this input
export const commonClassName =
  'w-full outline-none bg-transparent px-6 cursor-pointer border border-transparent'

export type TextEditableCellProps<T extends object> = {
  value: string | undefined
  parsedValue?: JSX.Element
  row: Row<T>
  column: Column<T>
  onCellChange: OnCellChange<T>
  hasAutoFocus?: boolean
  validations?: Validations
  className?: string
  inputType?: 'input' | 'textarea'
  postValueElement?: JSX.Element
}

const TextEditableCell = <T extends object>({
  value: initialValue,
  parsedValue,
  row: { index, original },
  column: { id, align },
  onCellChange, // This is a custom function that we supplied to our table instance
  hasAutoFocus,
  validations,
  className,
  inputType = 'input',
  postValueElement,
}: TextEditableCellProps<T>) => {
  const [value, setValue] = useState<string | undefined>(initialValue)
  const [hasFocus, setHasFocus] = useState(false)
  const textAreaRef = useRef<HTMLTextAreaElement>(null)
  const {
    error: inputError,
    validate,
    resetError,
  } = useValidationError(validations)

  const hasRtlChars = containsRtlChars(initialValue)

  const onChange: (newValue: string | undefined) => void = (newValue) => {
    const currentValue = value

    if (currentValue?.split('〽').length !== newValue?.split('〽').length) {
      return
    }
    validate(newValue)
    setValue(newValue ? replaceLineBreakAsNewLineEmoji(newValue) : undefined)
  }

  const onBlur = () => {
    if (!id) {
      throw new Error('The column id is missing')
    }

    if (!inputError) {
      onCellChange({
        rowIndex: index,
        columnId: id,
        value,
        rowOriginal: original,
      })
    } else {
      setValue(initialValue)
      resetError()
    }
    setHasFocus(false)
  }

  useEffect(() => {
    setValue(initialValue)
  }, [initialValue])

  const hasOverallFocus = hasFocus || hasAutoFocus

  useEffect(() => {
    if (textAreaRef.current && hasOverallFocus) {
      textAreaRef.current.style.height = '0px'

      const textAreaHeight = textAreaRef.current?.scrollHeight
      textAreaRef.current.style.height = `${textAreaHeight + 2}px`
    }
  }, [hasOverallFocus, textAreaRef, value])

  return (
    <div
      className="w-full"
      onClick={() => setHasFocus(true)}
      onBlur={() => setHasFocus(false)}
      dir={hasRtlChars ? 'rtl' : 'ltr'}
    >
      {hasOverallFocus ? (
        <>
          {inputType === 'textarea' ? (
            <textarea
              autoFocus={hasOverallFocus}
              value={replaceNewLineEmojiAsString(value || '')}
              onChange={(event) => onChange(event.target.value)}
              onBlur={onBlur}
              ref={textAreaRef}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  onBlur()
                }
              }}
              className={cx(
                commonClassName,
                `block w-full resize-none overflow-hidden`,
                {
                  'text-right': align === 'right',
                  'text-center': align === 'center',
                  'text-base': hasRtlChars,
                },
                className
              )}
            />
          ) : (
            <input
              autoFocus={hasOverallFocus}
              value={value}
              onChange={(event) => onChange(event.target.value)}
              onBlur={onBlur}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  onBlur()
                }
              }}
              className={cx(
                commonClassName,
                `block w-full`,
                {
                  'text-right': align === 'right',
                  'text-center': align === 'center',
                  'h-full': !inputError,
                  'text-base': hasRtlChars,
                },
                className
              )}
            />
          )}

          {inputError && (
            <div className="text-xs text-red-400">{inputError}</div>
          )}
        </>
      ) : (
        <div
          dir={hasRtlChars ? 'rtl' : 'ltr'}
          className={cx(
            commonClassName,
            `flex items-center w-full h-full whitespace-pre-line`,
            {
              'text-right': align === 'right',
              'text-center': align === 'center',
              'text-base': hasRtlChars,
            },
            className
          )}
        >
          {parsedValue || replaceNewLineEmojiAsString(value || '')}
          <span
            className={cx('flex-none', {
              'mr-4': hasRtlChars,
              'ml-4': !hasRtlChars,
            })}
          >
            {postValueElement}
          </span>
        </div>
      )}
    </div>
  )
}

export default TextEditableCell
